import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Dialog } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Field, Select } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { useWeekdayNames } from '../analytics';
import {
  REPORT_OUTPUT_FORMATS,
  REPORT_SCHEDULE_FREQUENCIES,
  type ReportOutputFormat,
  type ReportSchedule,
  type ReportScheduleFrequency,
} from '../reporting-types';
import { createSchedule, useSavedReports } from '../reportingApi';
import { apiMessage } from '../lib';
import { StaffMultiSelect } from './StaffMultiSelect';

/**
 * Create a delivery schedule. `runAsUserId` is never a field here — the
 * backend sets it to whoever is signed in and refuses to accept one from a
 * body at all (`CreateScheduleDto` has no such property), which is exactly
 * ADR-0013 §2.6's point: to run a schedule under somebody else's authority,
 * that somebody creates the schedule.
 */
export function ScheduleFormDialog({
  open,
  onClose,
  onCreated,
  defaultSavedReportId,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (schedule: ReportSchedule) => void;
  defaultSavedReportId?: string;
}) {
  const { t } = useTranslation();
  const weekdayName = useWeekdayNames();

  const { reports } = useSavedReports({ limit: 100 });

  const [name, setName] = useState('');
  const [savedReportId, setSavedReportId] = useState('');
  const [frequency, setFrequency] = useState<ReportScheduleFrequency>('DAILY');
  const [hour, setHour] = useState(6);
  const [minute, setMinute] = useState(0);
  const [dayOfWeek, setDayOfWeek] = useState(0);
  const [dayOfMonth, setDayOfMonth] = useState(1);
  const [timezone, setTimezone] = useState(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
  );
  const [format, setFormat] = useState<ReportOutputFormat>('XLSX');
  const [linkTtlHours, setLinkTtlHours] = useState(168);
  const [recipientIds, setRecipientIds] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setName('');
    setSavedReportId(defaultSavedReportId ?? '');
    setFrequency('DAILY');
    setHour(6);
    setMinute(0);
    setDayOfWeek(0);
    setDayOfMonth(1);
    setFormat('XLSX');
    setLinkTtlHours(168);
    setRecipientIds([]);
    setError(null);
  }, [open, defaultSavedReportId]);

  const options = useMemo(() => reports.map((r) => ({ id: r.id, name: r.name })), [reports]);

  const submit = async () => {
    if (!name.trim() || !savedReportId) return;
    setSaving(true);
    setError(null);
    try {
      const schedule = await createSchedule({
        name: name.trim(),
        savedReportId,
        frequency,
        hour,
        minute,
        dayOfWeek: frequency === 'WEEKLY' ? dayOfWeek : undefined,
        dayOfMonth: frequency === 'MONTHLY' ? dayOfMonth : undefined,
        timezone,
        format,
        linkTtlHours,
        recipientUserIds: recipientIds,
      });
      toast.success(t('reporting.schedules.created', { name: schedule.name }));
      onCreated(schedule);
      onClose();
    } catch (err) {
      setError(apiMessage(err, t('reporting.errors.saveFailed')));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t('reporting.schedules.create')}
      description={t('reporting.schedules.createHint')}
      className="max-w-lg"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button onClick={submit} disabled={saving || !name.trim() || !savedReportId}>
            {t('common.create')}
          </Button>
        </div>
      }
    >
      <div className="max-h-[70vh] space-y-4 overflow-y-auto pe-1">
        {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}

        <Field label={t('reporting.schedules.name')} required>
          <Input value={name} onChange={(event) => setName(event.target.value)} maxLength={120} />
        </Field>

        <Field label={t('reporting.schedules.report')} required>
          <Select value={savedReportId} onChange={(event) => setSavedReportId(event.target.value)}>
            <option value="">{t('reporting.schedules.pickReport')}</option>
            {options.map((option) => (
              <option key={option.id} value={option.id}>
                {option.name}
              </option>
            ))}
          </Select>
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label={t('reporting.schedules.frequency')}>
            <Select
              value={frequency}
              onChange={(event) => setFrequency(event.target.value as ReportScheduleFrequency)}
            >
              {REPORT_SCHEDULE_FREQUENCIES.map((value) => (
                <option key={value} value={value}>
                  {t(`reporting.frequency.${value}`)}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={t('reporting.schedules.timezone')} hint={t('reporting.schedules.timezoneHint')}>
            <Input value={timezone} onChange={(event) => setTimezone(event.target.value)} />
          </Field>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <Field label={t('reporting.schedules.hour')}>
            <Input
              type="number"
              min={0}
              max={23}
              value={hour}
              onChange={(event) => setHour(Number(event.target.value))}
            />
          </Field>
          <Field label={t('reporting.schedules.minute')}>
            <Input
              type="number"
              min={0}
              max={59}
              value={minute}
              onChange={(event) => setMinute(Number(event.target.value))}
            />
          </Field>
          {frequency === 'WEEKLY' && (
            <Field label={t('reporting.schedules.dayOfWeek')}>
              <Select value={dayOfWeek} onChange={(event) => setDayOfWeek(Number(event.target.value))}>
                {[0, 1, 2, 3, 4, 5, 6].map((day) => (
                  <option key={day} value={day}>
                    {weekdayName(day)}
                  </option>
                ))}
              </Select>
            </Field>
          )}
          {frequency === 'MONTHLY' && (
            <Field label={t('reporting.schedules.dayOfMonth')}>
              <Input
                type="number"
                min={1}
                max={31}
                value={dayOfMonth}
                onChange={(event) => setDayOfMonth(Number(event.target.value))}
              />
            </Field>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label={t('reporting.export.format')}>
            <Select value={format} onChange={(event) => setFormat(event.target.value as ReportOutputFormat)}>
              {REPORT_OUTPUT_FORMATS.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={t('reporting.schedules.linkTtl')} hint={t('reporting.schedules.linkTtlHint')}>
            <Input
              type="number"
              min={1}
              max={720}
              value={linkTtlHours}
              onChange={(event) => setLinkTtlHours(Number(event.target.value))}
            />
          </Field>
        </div>

        <Field label={t('reporting.schedules.recipients')} hint={t('reporting.schedules.recipientsHint')}>
          <StaffMultiSelect selected={recipientIds} onChange={setRecipientIds} />
        </Field>
      </div>
    </Dialog>
  );
}
